import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useMe } from '../App.js';
import { api } from '../api.js';
import { useI18n } from '../i18n.js';
import { safeCliCallback } from '../oauth.js';

/**
 * Browser login for the CLI: `p2b login` opens /cli-login?port=…&state=…, the signed-in person allows it, the page
 * creates an API key and hands it to the CLI's loopback listener.
 */
export function CliLogin() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const me = useMe();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const callback = safeCliCallback(params.get('port'), params.get('state'));
  if (me.isPending) return <div className="center muted">{t('loading')}</div>;
  if (me.error)
    return (
      <Navigate to={`/login?next=${encodeURIComponent(`/cli-login${window.location.search}`)}`} replace />
    );
  if (!callback)
    return (
      <p className="error" role="alert">
        {t('badLink')}
      </p>
    );

  async function allow() {
    try {
      const host = params.get('host') ?? 'p2b';
      const key = await api.createKey({
        name: `p2b CLI (${host.split('.')[0]!.slice(0, 40)})`,
        scope: 'write',
        expiresInDays: 365,
      });
      setDone(true);
      const u = new URL(callback!);
      u.searchParams.set('key', key.key);
      u.searchParams.set('state', params.get('state')!);
      window.location.assign(u.toString());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="stack">
      <h2 className="bare-title">{t('cliTitle')}</h2>
      <p>{t('cliText')}</p>
      <p className="muted">{me.data.user.email}</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done ? (
        <p className="sent">{t('cliDone')}</p>
      ) : (
        <div className="row">
          <button className="primary" onClick={allow}>
            {t('allow')}
          </button>
          <button onClick={() => window.close()}>{t('deny')}</button>
        </div>
      )}
    </div>
  );
}
